import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HelpSection = ({ section, searchQuery }) => {
  // Highlight search terms in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    return text?.split(regex)?.map((part, index) => 
      regex?.test(part) ? (
        <span key={index} className="bg-warning/20 text-warning-foreground px-1 rounded">
          {part}
        </span>
      ) : part
    );
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Section Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
            <Icon name={section?.icon} size={24} className="text-accent-foreground" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-text-primary">{section?.name} Section</h2>
            <p className="text-text-secondary">{section?.description}</p>
          </div>
        </div>
        
        <div className="bg-muted rounded-lg p-4">
          <p className="text-text-primary">
            {highlightText(section?.content?.overview, searchQuery)}
          </p>
        </div>
      </div>
      {/* Step-by-Step Instructions */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-text-primary mb-6">Step-by-Step Instructions</h3>
        
        <div className="space-y-6">
          {section?.content?.steps?.map((step, index) => (
            <div key={index} className="relative">
              {/* Step Number */}
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-semibold text-accent-foreground">
                    {index + 1}
                  </span>
                </div>
                
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-text-primary mb-2">
                    {highlightText(step?.title, searchQuery)}
                  </h4>
                  
                  <p className="text-text-secondary mb-4">
                    {highlightText(step?.description, searchQuery)}
                  </p>
                  
                  {/* Tips */}
                  <div className="bg-accent/5 rounded-lg p-4 border-l-4 border-accent">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Lightbulb" size={16} className="text-accent" />
                      <span className="text-sm font-medium text-accent">Tips & Best Practices</span>
                    </div>
                    
                    <ul className="space-y-2">
                      {step?.tips?.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start space-x-2">
                          <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-text-secondary">
                            {highlightText(tip, searchQuery)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Connecting Line */}
              {index < section?.content?.steps?.length - 1 && (
                <div className="absolute left-4 top-10 w-px h-6 bg-border"></div>
              )}
            </div>
          ))}
        </div>

        {/* Validation Rules */}
        <div className="mt-8 bg-destructive/5 rounded-lg p-4 border-l-4 border-destructive">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="AlertTriangle" size={16} className="text-destructive" />
            <span className="text-sm font-medium text-destructive">Validation Requirements</span>
          </div>
          
          <ul className="space-y-2">
            {section?.content?.validation?.map((rule, ruleIndex) => (
              <li key={ruleIndex} className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={14} className="text-destructive mt-0.5 flex-shrink-0" />
                <span className="text-sm text-text-secondary">
                  {highlightText(rule, searchQuery)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            variant="default"
            iconName="Edit"
            iconPosition="left"
            iconSize={16}
            onClick={() => window.open('/artist-content-management', '_blank')}
          >
            Edit {section?.name} Section
          </Button>
          
          <Button
            variant="outline"
            iconName="ExternalLink"
            iconPosition="left"
            iconSize={16}
            onClick={() => window.open('/mobile-app-preview', '_blank')}
          >
            Preview Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpSection;